// Base API URL for all API endpoints
export const API_BASE_URL = "https://netpriceestimate.com";

// Entities API URL for dynamic entity fetching
export const ENTITIES_API_URL = "https://netpriceestimate.com/api/entities";

// External API URLs for dynamic data fetching
export const EXTERNAL_API_URLS = {
  HIGH_SCHOOLS: "https://netpriceestimate.com/api/highschools",
  STATES: "https://netpriceestimate.com/api/states",
} as const;

// Shared constants for controls
export const CONTROL_NAME_WELCOME = "Welcome";
export const FAQ_SUFFIX = " - FAQ";

// Example constants for conditional styling (if needed elsewhere)
export const CLIENT_NAME_UAA = "University of Alaska Anchorage";
export const CLIENT_NAME_HCRC = "Human Capital Research Corporation";

// --- GOOGLE TAG MANAGER CONFIGURATION ---

// GTM Configuration Constants
export const GLOBALGTM_ID: string = "GTM-5HMS4W7X";

// Dynamic, client-provided Privacy Policy URL
export const PRIVACYPOLICY_URL: string =
  "https://humancapital.com/net-price-calculator-privacy-policy/";

// Consent Storage Configuration
export const CONSENTSTORAGE_KEY: string = "hce_user_consent";
export const CONSENTEXPIRATION_MS: number = 365 * 24 * 60 * 60 * 1000; // 12 months

// Enum for entity names
export enum ENTITY_NAMES {
  STUDENT_INFORMATION = "StudentInformation",
  STUDENT_CONTACT = "StudentContact",
  ACADEMIC_SCORES = "AcademicScores",
  FAMILY_INFORMATION = "FamilyInformation",
  PARENT_FINANCES = "ParentFinances",
  STUDENT_FINANCES = "StudentFinances",
  SUMMARY = "Summary",
  WELCOME = "Welcome",
  ESTIMATE = "Estimate",
}

// Field names for dynamic form handling
export const FIELD_NAMES = {
  SAI: "Student Aid Index",
  STATE: "State",
  STUDENT_CITIZENSHIP: "Student Citizenship",
  HIGH_SCHOOL: "High School",
  HOMESCHOOL: "homeschool",
} as const;

// Boolean string values
export const BOOLEAN_VALUES = {
  TRUE: "True",
  FALSE: "False",
  YES: "Yes",
  NO: "No",
  TRUE_LOWER: "true",
  FALSE_LOWER: "false",
  YES_LOWER: "yes",
  NO_LOWER: "no",
} as const;

// Country/Location values
export const LOCATION_VALUES = {
  USA: "USA",
  USA_LOWER: "usa",
} as const;

// Attribute names for dynamic attribute detection
export const ATTRIBUTE_NAMES = {
  CONNECT_WITH_US: "Connect With Us",
} as const;

// Field patterns for dynamic detection
export const FIELD_PATTERNS = {
  SKIP_FINANCIAL_AID: [
    "need-based",
    "calculate",
    "financial aid",
    "international",
    "ineligible",
  ],
  CALCULATE_NEED_BASED_AID: ["help me calculate", "need-based financial aid"],
} as const;

// Aid calculation options
export const AID_CALCULATION_OPTIONS = {
  SAI: "I already have my Student Aid Index (SAI)",
  NEED_BASED: "Help me calculate my need-based financial aid",
  SKIP_INTERNATIONAL:
    "I am an international citizen ineligible for need-based financial aid",
  SKIP_NO_NEED:
    "I do not want to calculate need-based financial aid; OR I am an international citizen ineligible for need-based financial aid",
} as const;

// UI messages
export const REQUIRED_FIELD_MESSAGE = "This field is required";

// Summary page attribute names
export const SUMMARY_ATTRIBUTE_NAMES = {
  NET_PRICE: "Your Estimated Net Price",
  PREHEADER_HIDDEN: "PreHeaderTextHidden",
  FOOTER2: "footer2",
} as const;

// Summary page formulas
export const SUMMARY_FORMULAS = {
  GRANT_POSITIVE: "need_based_grant_estimate>0",
  GRANT_ZERO: "need_based_grant_estimate==0",
} as const;

// Summary page text markers for tenant detection
export const SUMMARY_TEXT_MARKERS = {
  WORK_STUDY: "Work-Study Program",
  RANDOLPH_FOOTER: "Randolph College is committed to each student",
  SAI_INDICATOR: "student aid index",
  LEBANON_VALLEY: "Lebanon Valley College students may be considered",
  TAMPA_FOOTER:
    "This estimate is meant to help you with your educational financing at The University of Tampa. It does not represent a final determination, or actual award, of admissibility, financial assistance or a final net price. Estimates are based on cost of attendance and financial aid provided to students in 2025-2026. This estimate shall not be binding on the Secretary of Education, the institution of higher education, or the state.",
  LOYOLA_FOOTER_OPTIONS12:
    "Qualified applicants may also be eligible for additional assistance through the Federal Work-Study Program. Additional information about this program is available on our website: www.loyola.edu/department/financial-aid/undergraduate/programs\n\nThis estimate is meant to help you with your educational financing at college. It does not represent a final determination, or actual award, of admissibility, financial assistance or a final net price. Estimates are based on cost of attendance and financial aid provided to students in 2025-2026. This estimate shall not be binding on the Secretary of Education, the institution of higher education, or the state.",
  LOYOLA_FOOTER_OPTION3:
    "This estimate is meant to help you with your educational financing at college. It does not represent a final determination, or actual award, of admissibility, financial assistance or a final net price. Estimates are based on cost of attendance and financial aid provided to students in 2025-2026. This estimate shall not be binding on the Secretary of Education, the institution of higher education, or the state.",
  VALPO_FOOTER_OPTION3:
    "This estimate is meant to help you with your educational financing at college. It does not represent a final determination, or actual award, of admissibility, financial assistance or a final net price. Estimates are based on cost of attendance and financial aid provided to students in 2025-2026. This estimate shall not be binding on the Secretary of Education, the institution of higher education, or the state.",
  VALPO_FOOTER_OPTIONS12:
    "Federal Parent Loans and Private Student Loans may also available to help with your remaining costs.\n\nThis estimate is meant to help you with your educational financing at college. It does not represent a final determination, or actual award, of admissibility, financial assistance or a final net price. Estimates are based on cost of attendance and financial aid provided to students in 2025-2026. This estimate shall not be binding on the Secretary of Education, the institution of higher education, or the state.",
  VALPO_IDENTIFIER: "Valparaiso University",
  WPI_HEADER_OPTIONS12:
    "To learn more about additional information related to Financial Aid, visit: First-Year Students | Worcester Polytechnic Institute",
  MMC_FOOTER_OPTIONS12:
    "The College offers an interest free monthly payment plan which allows a family to budget payments over the course of the entire semester. You can find out more information about MMC's payment plan at : www.mmm.edu/offices/center-for-student-services/make-a-payment\n\nIn addition to the low-cost Direct Student Loan estimated, you may qualify for Parent PLUS and alternative/private loans up to the cost of attendance. These type of loans require a credit check. Unlike scholarships and grants, loans must be paid back with interest and you are under no obligation to borrow. You can find out more about Federal Loans at: studentaid.gov/understand-aid/types/loans.\n\nThis estimate is meant to help you with your educational financing at MMC. It does not represent a final determination, actual offer, or a final net price. Estimates are based on cost of attendance and financial aid provided to students in 2025-2026. This estimate shall not be binding on the Secretary of Education, the institution of higher education, or the state. You must complete the Free Application for Federal Student Aid in order to be eligible for, and receive, an actual financial aid award that includes Federal grant, loan, or work-study assistance under Title IV.",
  MMC_FOOTER_OPTION3:
    "This estimate is meant to help you with your educational financing at MMC. It does not represent a final determination, actual offer, or a final net price. Estimates are based on cost of attendance and financial aid provided to students in 2025-2026. This estimate shall not be binding on the Secretary of Education, the institution of higher education, or the state. You must complete the Free Application for Federal Student Aid in order to be eligible for, and receive, an actual financial aid award that includes Federal grant, loan, or work-study assistance under Title IV.",
} as const;
